import { useCallback, useEffect, useMemo, useState } from "react"

import * as turf from "@turf/turf"
import { useMatch } from "react-router-dom"

import { CityMapLegend } from "@l2r-front/l2r-app-city"
import { MapWithPhotoViewer } from "@l2r-front/l2r-common"
import { useImageCollectionStateContext, useImageCollectionDispatchContext } from "@l2r-front/l2r-images"
import { ReactMapGl, useMapContext } from "@l2r-front/l2r-map"
import { useIsFetchingNetwork, useNetworksStateContext, useRoadBoundingBox } from "@l2r-front/l2r-networks"
import { PropTypes } from "@l2r-front/l2r-proptypes"
import { APP_CAMPAIGN, MODULE_CAMPAIGNS, useCampaign } from "@l2r-front/l2r-shared-campaign"
import { useIsMobileDevice } from "@l2r-front/l2r-ui"

import { appCampaign } from "../../constants/appCampaign"

export function Map(props: any) {

    const {
        children,
        mapProps,
        ...otherProps
    } = props

    const isFetchingNetwork = useIsFetchingNetwork()
    const isFetching = isFetchingNetwork
    const { selectedNetwork } = useNetworksStateContext()
    const [mapState, mapDispatch] = useMapContext()
    const { onOpenImageViewer, onSwitchImageLayer, isImageCollectionAvailable } = useImageCollectionDispatchContext()
    const { imageLayerDisplayed } = useImageCollectionStateContext()
    const { setMapBoundingBoxToFit } = mapDispatch
    const { error } = mapState
    const isMobile = useIsMobileDevice()
    const matchCampaignDetail = useMatch(`:networkId/${APP_CAMPAIGN}/${MODULE_CAMPAIGNS}/:campaignId`)
    const campaignId = matchCampaignDetail?.params.campaignId
    const { data: campaign } = useCampaign(campaignId)

    const mapInitialViewState = useMemo(() => {
        const [minLng, minLat, maxLng, maxLat] = turf.bbox(selectedNetwork.boundingBox)
        return {
            longitude: (minLng + maxLng) / 2,
            latitude: (minLat + maxLat) / 2,
            zoom: 8,
        }
    }, [selectedNetwork])

    const onRecenterOnNetworkBoundingBox = useCallback(() => {
        const [minLng, minLat, maxLng, maxLat] = turf.bbox(selectedNetwork.boundingBox)
        setMapBoundingBoxToFit({
            minLng,
            minLat,
            maxLng,
            maxLat,
        })
    }, [selectedNetwork, setMapBoundingBoxToFit])

    const [searchedRoad, setSearchedRoad] = useState<string | null>(null)
    const roadBoundingBox = useRoadBoundingBox(searchedRoad)

    useEffect(() => {
        if (searchedRoad && roadBoundingBox) {
            setMapBoundingBoxToFit(roadBoundingBox)
        }
    }, [searchedRoad, roadBoundingBox, setMapBoundingBoxToFit])

    const handleRoadSearch = useCallback((roadId: string) => {
        setSearchedRoad(roadId)
    }, [])

    const onSwitchImageLayerCallback = isImageCollectionAvailable() ? onSwitchImageLayer : undefined
    const match = useMatch("/:networkId/:modules/:subModule/*")
    const subModule = match?.params.subModule
    const module = Object.values(appCampaign.modules).find(m => m.path === subModule)?.id

    return (
        <MapWithPhotoViewer
            dragRotate={false}
            error={error}
            initialViewState={mapInitialViewState}
            isLoading={isFetching}
            imageLayerDisplayed={imageLayerDisplayed}
            module={module}
            onOpenImageViewer={onOpenImageViewer}
            onSwitchImageLayer={onSwitchImageLayerCallback}
            onRecenter={onRecenterOnNetworkBoundingBox}
            onRoadSelected={handleRoadSearch}
            topLeftElements={(!isMobile && !!campaign) && [<CityMapLegend module={campaign.campaign_type} key="city-map-legend" />]}
            topRightElements={(isMobile && !!campaign) && [<CityMapLegend module={campaign.campaign_type} key="city-map-legend" />]}
            {...mapProps}
            {...otherProps}
        >
            {children}
        </MapWithPhotoViewer>
    )
}

Map.propTypes = {
    ...ReactMapGl.propTypes,
    className: PropTypes.string,
}